
import { supabase } from "@/integrations/supabase/client";
import { isPast, parseISO, addDays } from "date-fns";

interface PollFinderResult {
  gameId: string | null;
  error: string | null;
}

/**
 * Checks whether a game has elapsed (date + 1 day buffer has passed).
 */
function hasGameElapsed(gameDate: string): boolean {
  const dayAfter = addDays(parseISO(gameDate), 1);
  return isPast(dayAfter);
}

/**
 * Finds the best game for a team's static URL using this priority:
 * 1. Pinned game (if set and not elapsed)
 * 2. Nearest upcoming game by date
 * 3. Most recent game with voting_open = true
 */
export const findLatestActivePoll = async (
  teamId: string,
  teamName: string
): Promise<PollFinderResult> => {
  try {
    console.log("PollFinder - Finding best game for team:", teamId, teamName);
    
    if (!teamId || typeof teamId !== 'string') {
      return {
        gameId: null,
        error: `Invalid team identifier. Please try again with a valid team.`
      };
    }

    // 1. Check for pinned game
    // Use SECURITY DEFINER RPC so anon lookups don't require a permissive
    // SELECT policy on teamvote_teams.
    const { data: teamRows } = await supabase.rpc('get_teamvote_team_public', {
      p_team_id: teamId,
    });
    const teamData = Array.isArray(teamRows) ? teamRows[0] || null : null;


    if (teamData?.pinned_game_id) {
      // Verify the pinned game exists and hasn't elapsed
      const { data: pinnedGame } = await supabase
        .from('teamvote_games')
        .select('id, date')
        .eq('id', teamData.pinned_game_id)
        .single();

      if (pinnedGame && !hasGameElapsed(pinnedGame.date)) {
        console.log("PollFinder - Using pinned game:", pinnedGame.id);
        return { gameId: pinnedGame.id, error: null };
      }
      console.log("PollFinder - Pinned game elapsed or missing, falling through");
    }

    // 2. Nearest upcoming game by date (game date >= today, regardless of voting_open)
    const today = new Date().toISOString().split('T')[0];
    const { data: upcomingGames } = await supabase
      .from('teamvote_games')
      .select('id, date')
      .eq('team_id', teamId)
      .gte('date', today)
      .order('date', { ascending: true })
      .limit(1);

    if (upcomingGames && upcomingGames.length > 0) {
      console.log("PollFinder - Using nearest upcoming game:", upcomingGames[0].id);
      return { gameId: upcomingGames[0].id, error: null };
    }

    // 3. Most recent game with voting_open = true
    const { data: openGames } = await supabase
      .from('teamvote_games')
      .select('id, date')
      .eq('team_id', teamId)
      .eq('voting_open', true)
      .order('created_at', { ascending: false })
      .limit(1);

    if (openGames && openGames.length > 0) {
      console.log("PollFinder - Using most recent voting-open game:", openGames[0].id);
      return { gameId: openGames[0].id, error: null };
    }

    return { 
      gameId: null, 
      error: `${teamName} has no active polls currently. Check back later or contact the coach.` 
    };
    
  } catch (error) {
    console.error("Error finding active poll:", error);
    return { 
      gameId: null, 
      error: `Error finding active polls for ${teamName}. Please try again later.` 
    };
  }
};
